/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	// UI Settings
	defaultOpenFilters: 1,
	
	// Price Slider Settings
	priceGap: 10,
	
	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		sortBy: '[data-sort-by]',
		sortByLabel: '[data-sort-by-label]',
		
		// Mobile filters
		mobileFilterWrapper: '[data-mobile-filters]',
		filterOverlay: '.mm-grid-second-container-filter-overlay',
		htmlElement: 'html',
		
		// Filter accordions
		filterHeaders: '[data-filter-header]',
		
		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Collection Event Emitter
const collectionEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'collection'
	};
	
	return document.dispatchEvent(new CustomEvent(`collection:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get sortBySelector() { return document.querySelector(COLLECTION_CONFIG.selectors.sortBy); },
	get sortByLabel() { return document.querySelector(COLLECTION_CONFIG.selectors.sortByLabel); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get mobileFilterWrapper() { return document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper); },
	get filterOverlay() { return document.querySelector(COLLECTION_CONFIG.selectors.filterOverlay); },
	
	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get filterHeaders() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },
	
	// Price sliders
	get priceSliders() {
		return {
			minRange: document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange: document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.minRangeValue),
			maxRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.maxRangeValue)
		};
	},
	
	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	}
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	// Guards against out-of-order AJAX responses: only the latest request is allowed to update the DOM
	_latestRequestId: 0,

	async request(url) {
		collectionEmit('api-start', { url });
		
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json') 
				? await response.json() 
				: await response.text();
			
			collectionEmit('api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			collectionEmit('api-error', { url, error: error.message });
			throw error;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	async refreshWithFilters(url_filters) {
		collectionEmit('filter-start', { filters: url_filters });

		// Each call gets its own id; if a newer call finishes first, this one's result is discarded on arrival
		const requestId = ++this._latestRequestId;

		try {
			const currentUrl = `${window.location.pathname}`;
			const sectionId = this.getSectionIdShort();

			// Use Shopify Section Rendering API
			const requestUrl = `${currentUrl}?${url_filters}&sections=${sectionId}`;
			const response = await this.request(requestUrl);

			if (requestId !== this._latestRequestId) {
				// A more recent filter request has since started — ignore this stale response
				return;
			}

			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			// Handle different response types
			let newSectionHtml;
			if (typeof response === 'object' && response[sectionId]) {
				// Section Rendering API JSON response
				newSectionHtml = response[sectionId];
			} else if (typeof response === 'string') {
				// Fallback: HTML response
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			} else {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			collectionEmit('filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });

		} catch (error) {
			if (requestId !== this._latestRequestId) return;

			collectionEmit('filter-error', { error: error.message, filters: url_filters });

			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-loading');
			}

			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		// Create temporary element to parse new HTML
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		// Update collection grid content (products only)
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
		}

		// Update active filters
		const newActiveFilters = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters);
		CollectionDOM.activeFilters.forEach((currentFilter, index) => {
			if (newActiveFilters[index]) {
				currentFilter.innerHTML = newActiveFilters[index].innerHTML;
			}
		});

		// Update other elements with 'data-collection-reload' attribute.
		// Matched by [data-reload-key], not position: the product-count and pagination
		// blocks are wrapped in an `unless collection.products_count == 0`, so they can
		// disappear from the response entirely (0 results) while the filter CTA button
		// stays put — positional index matching would then pair the wrong elements up.
		CollectionDOM.reloadElements.forEach((currentElem) => {
			const key = currentElem.dataset.reloadKey;
			const newElem = key
				? tempEl.querySelector(`[data-reload-key="${key}"]`)
				: null;

			if (!newElem) {
				// No longer present in the filtered result (e.g. 0 products) — hide rather
				// than remove, so the node is still there to restore once results return.
				currentElem.style.display = 'none';
				return;
			}

			currentElem.style.display = '';

			// mm-btn markup duplicates the label in two [data-btn-label] spans (visible + aria-hidden, for the hover mask animation)
			const currentLabels = currentElem.querySelectorAll('[data-btn-label]');
			const newLabels = newElem.querySelectorAll('[data-btn-label]');

			if (currentLabels.length && currentLabels.length === newLabels.length) {
				currentLabels.forEach((currentLabel, labelIndex) => {
					currentLabel.textContent = newLabels[labelIndex].textContent;
				});
			} else {
				// Fallback: update entire innerHTML for simple elements
				currentElem.innerHTML = newElem.innerHTML;
			}
		});

		// Update URL in history without reloading page
		const newUrl = `${currentUrl}?${url_filters}`;
		window.history.replaceState(null, document.title, newUrl);
		
		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-loading');
		}
		
		// Re-initialize filters after DOM update
		CollectionFilters.init();
		
		// Re-initialize add to cart forms after DOM update
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	init() {
		if (!CollectionDOM.form) {
			collectionEmit('form-error', { error: 'Collection facets form not found' });
			return;
		}
		
		const inputs = CollectionDOM.formInputs;
		
		// Add event listeners to inputs
		inputs.forEach(input => {
			// Remove existing listeners to avoid duplicates
			input.removeEventListener('change', this.handleInputChange);
			input.addEventListener('change', this.handleInputChange);
		});
		
		collectionEmit('filters-initialized', { inputCount: inputs.length });
	},

	handleInputChange(event) {
		collectionEmit('input-change', {
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked
		});

		CollectionFilters.debouncedSubmitForm();
	},

	// Debounced so checking several filters in quick succession only fires the last request
	debouncedSubmitForm: window.debounce(() => CollectionFilters.submitForm(), COLLECTION_CONFIG.debounceDelay),

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			collectionEmit('form-error', { error: 'Required elements not found' });
			return;
		}
		
		try {
			collectionEmit('form-submit-start');
			
			const formData = new FormData(CollectionDOM.form);
			const url_filters = new URLSearchParams(formData).toString();
			
			CollectionDOM.grid.classList.add('mm-loading');
			
			await CollectionAPI.refreshWithFilters(url_filters);
			
			collectionEmit('form-submit-success', { filters: url_filters });
			
		} catch (error) {
			collectionEmit('form-submit-error', { error: error.message });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-loading');
			}
		}
	}
};

/*********************************************************************************************
 * 📱 MOBILE FILTERS
 */
const MobileFilters = {
	_previousFocus: null,
	_isOpen: false,

	open(trigger) {
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const overlay = CollectionDOM.filterOverlay;
		const html = CollectionDOM.html;

		if (!wrapperMobile || !html) return;

		this._previousFocus = trigger || document.activeElement;
		this._isOpen = true;

		wrapperMobile.classList.add('mm-open');
		if (overlay) overlay.classList.add('mm-open');
		html.classList.add('stop-scrolling');

		const closeBtn = wrapperMobile.querySelector('[data-filter-close]');
		if (closeBtn) closeBtn.focus();

		collectionEmit('mobile-filters-opened');
	},

	close() {
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const overlay = CollectionDOM.filterOverlay;
		const html = CollectionDOM.html;

		if (!wrapperMobile || !html) return;

		wrapperMobile.classList.remove('mm-open');
		if (overlay) overlay.classList.remove('mm-open');
		html.classList.remove('stop-scrolling');

		this._isOpen = false;

		if (this._previousFocus) {
			this._previousFocus.focus();
			this._previousFocus = null;
		}

		collectionEmit('mobile-filters-closed');
	},

	checkDrawer() {
		const hasOpenFilters = document.location.href.includes('open_filters');
		if (hasOpenFilters) {
			this.open();
		}
	}
};

/*********************************************************************************************
 * 🎛️ FILTER ACCORDIONS
 */
const FilterAccordions = {
	init() {
		const filtersHeaders = CollectionDOM.filterHeaders;

		filtersHeaders.forEach((filtersHeader, index) => {
			// Activate first three filters by default
			if (index < COLLECTION_CONFIG.defaultOpenFilters) {
				filtersHeader.classList.add('mm-active');
			}

			filtersHeader.setAttribute('aria-expanded', String(filtersHeader.classList.contains('mm-active')));

			// Add click event to open/close filters
			const toggleFilter = function() {
				filtersHeader.classList.toggle('mm-active');
				const isActive = filtersHeader.classList.contains('mm-active');
				filtersHeader.setAttribute('aria-expanded', String(isActive));
				collectionEmit('filter-accordion-toggle', { index, active: isActive });
			};

			filtersHeader.addEventListener('click', toggleFilter);

			filtersHeader.addEventListener('keydown', function(e) {
				if (e.key !== 'Enter' && e.key !== ' ') return;
				e.preventDefault();
				toggleFilter();
			});
		});
		
		collectionEmit('accordions-initialized', { count: filtersHeaders.length });
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return CollectionDOM.priceSliders;
	},
	
	isReady() {
		const elements = this.elements;
		return elements.minRange && elements.maxRange && 
				elements.sliderTrack && elements.minRangeValue && 
				elements.maxRangeValue;
	},

	init() {
		if (!this.isReady()) return;
		
		const { minRange, maxRange } = this.elements;
		
		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});
		
		// Initial update
		this.updateRange();
		collectionEmit('price-sliders-initialized');
	},

	updateRange() {
		if (!this.isReady()) {
			collectionEmit('price-error', { error: 'Price slider elements not found' });
			return;
		}
		
		const { minRange, maxRange, sliderTrack, minRangeValue, maxRangeValue } = this.elements;
		
		const minValue = parseInt(minRange.value, 10); 
		const maxValue = parseInt(maxRange.value, 10);

		// Update min and max values display
		const minValueEl = minRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);
		
		if (minValueEl) minValueEl.innerHTML = minValue;
		if (maxValueEl) maxValueEl.innerHTML = maxValue;
		
		// Calculate percentages for track background
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;

		// Update track background to reflect selected price range
		sliderTrack.style.background = `linear-gradient(to right, #EDDBDB ${minPercent}%, var(--black-text) ${minPercent}%, var(--black-text) ${maxPercent}%, #EDDBDB ${maxPercent}%)`;

		// Move the thumb marker (::after) along the track without shifting the label text
		minRangeValue.style.setProperty('--thumb-percent', `${minPercent}%`);
		maxRangeValue.style.setProperty('--thumb-percent', `${maxPercent}%`);

		collectionEmit('price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (for backward compatibility)
 */
window.mmSortByChanged = (select) => {
	if (!select) {
		collectionEmit('sort-error', { error: 'Sort by elements not found' });
		return;
	}

	const targetRadio = CollectionDOM.form?.querySelector(`input[name="sort_by"][value="${CSS.escape(select.value)}"]`);
	if (targetRadio) targetRadio.checked = true;

	const sortByLabel = CollectionDOM.sortByLabel;
	const selectedOption = select.options[select.selectedIndex];
	if (sortByLabel && selectedOption) {
		sortByLabel.textContent = selectedOption.text.trim();
	}

	CollectionFilters.submitForm();
};

// Mobile filter functions
window.mmOpenFiltersMobile = (trigger) => MobileFilters.open(trigger);
window.mmCloseFiltersMobile = () => MobileFilters.close();
window.mmCheckDrawer = () => MobileFilters.checkDrawer();

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	collectionEmit('init-start');
	
	try {
		CollectionFilters.init();
		FilterAccordions.init();
		PriceSliders.init();
		MobileFilters.checkDrawer();
		
		collectionEmit('init-success');
	} catch (error) {
		collectionEmit('init-error', { error: error.message });
		window.log('error', 'Collection', 'Collection initialization failed', { error: error.message });
	}
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	// Filter close (delegated — the panel now has both a close button and an overlay,
	// both carrying [data-filter-close])
	document.addEventListener('click', (e) => {
		if (!e.target.closest('[data-filter-close]')) return;
		if (typeof window.mmCloseFiltersMobile === 'function') {
			window.mmCloseFiltersMobile();
		}
	});

	// Escape key closes the filter panel
	document.addEventListener('keydown', (e) => {
		if (e.key === 'Escape' && MobileFilters._isOpen) {
			MobileFilters.close();
		}
	});

	// Filter submit button
	const filterSubmitBtn = document.querySelector('[data-filter-submit]');
	if (filterSubmitBtn) {
		filterSubmitBtn.addEventListener('click', () => {
			CollectionFilters.submitForm();
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		});
	}

	// Open filters mobile buttons
	const openFiltersBtns = document.querySelectorAll('[data-open-filters-mobile]');
	openFiltersBtns.forEach((btn) => {
		btn.addEventListener('click', () => {
			if (typeof window.mmCloseMobileMenu === 'function') {
				window.mmCloseMobileMenu();
			}
			if (typeof window.mmOpenFiltersMobile === 'function') {
				window.mmOpenFiltersMobile(btn);
			}
		});
	});
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			console.log(`🏷️ [COLLECTION] ${event}:`, e.detail)
		);
	});
}